import { Tooltip } from '@nextui-org/react';
import Rive from '@rive-app/react-canvas';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import React from 'react';

import { IconEyeCard, IconInfoMarketPlace, IconVerify16 } from '@/assets';
import { CardRenderFollowType } from '@/components/card';
import RenderWhen from '@/components/RenderWhen';
import { ListSource } from '@/configs/constant';
import { TokenDenom } from '@/hooks/useTokenPrices';
import useToUsdWithDenom from '@/hooks/useToUsdWithDenom';
import { useLayout } from '@/provider/LayoutProvider';
import { useRouter } from '@/provider/RouterProvider';
import type { NftMarket } from '@/types';
import { convertRoyaltyFromContract, formatNumberSocial, isRivFile } from '@/utils';
import { currencyFormatter } from '@/utils/currency';

import useMarketplaceCard from './useMarketplaceCard';

const DynamicNsfwMark = dynamic(() => import('@/components/NsfwMark'));

const MarketPlaceCard: React.FC<{
  nftMarket: NftMarket;
}> = ({ nftMarket }) => {
  const { getPriceUsdWithDenom } = useToUsdWithDenom();
  const { setIsLoadingBar } = useLayout();
  const { isOwner, isNsfw, isPlusUser } = useMarketplaceCard(nftMarket);
  const linkURL =
    nftMarket.asset.prompt !== null
      ? `/prompt/${nftMarket.asset.prompt}`
      : `/artwork/${nftMarket.asset.id}`;
  const router = useRouter();

  return (
    <Link
      onClick={() => setIsLoadingBar(true)}
      href={linkURL}
      className="group block h-full w-full flex-col rounded-[16px] bg-white p-2 shadow-[0px_4px_8px_4px_rgba(199,205,231,0.25)]"
    >
      <div className=" h-full w-full">
        <div className="relative w-full before:block before:pb-[100%]">
          <div className="absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-[12px] bg-gray-50 object-cover">
            {isRivFile(nftMarket.asset.url) ? (
              <Rive src={nftMarket.asset.url} stateMachines="state-machine" />
            ) : (
              <CardRenderFollowType
                isPlayIcon
                disabledClick
                asset={{ ...nftMarket.asset, url: nftMarket.asset.picture ?? nftMarket.asset.url }}
                isNotBorder
                isAudioBorder={false}
                listSource={ListSource.marketplace_nft}
              />
            )}
            {isNsfw && <DynamicNsfwMark isPlus={isPlusUser} showPositionBottom={75} />}
            <div className="absolute inset-x-0 top-0 z-[11] mt-[10px] text-white">
              <div className="inline-flex w-[100%] justify-between px-[10px]">
                <div className="flex items-center ">
                  {nftMarket.asset.collection?.id && nftMarket.asset.prompt === null && (
                    <div
                      className=" ml-[4px] items-center rounded-[100px] bg-black/50 px-[10px] py-[3.5px] text-[12px] font-semibold text-white opacity-0 transition  delay-100 ease-in-out group-hover:opacity-100"
                      onClick={(event) => {
                        event.preventDefault();
                        router.push(`/collection/${nftMarket.asset.collection.id}`);
                      }}
                    >
                      {nftMarket.asset.collection.name}
                    </div>
                  )}
                </div>
                {/* <div className="flex items-center">
                <div className="">
                  <Button
                    onClick={isLike ? handleUnlike : handleLike}
                    isIconOnly
                    size="sm"
                    radius="full"
                    className={`flex h-[25px] w-[100%] items-center justify-center bg-black/50 px-[8px] font-sans ${
                      !isLike ? 'bg-black/50' : 'bg-[#DA613A66]'
                    }`}
                  >
                    {isLike ? (
                      <IconHeartActive className="items-center justify-center" />
                    ) : (
                      <IconHeart className="items-center justify-center" />
                    )}
                    <span className="items-center justify-center pl-[4px] text-[12px] text-white">
                      {numberLike}
                    </span>
                  </Button>
                </div>
              </div> */}
              </div>
            </div>
          </div>
        </div>
        <div className="text-black">
          <div className="mt-[10px] flex items-center justify-between px-2">
            <div>
              <p className="text-[12px] text-gray-500">Owner</p>
              <div className="flex items-center gap-[2px]">
                <p className="line-clamp-1 max-w-[80px] gap-1 text-[13px] font-semibold leading-[1.6]">
                  {nftMarket.asset.provider.username}
                </p>
                {nftMarket.asset.provider.sVerify === 'success' && (
                  <span>
                    <IconVerify16 />
                  </span>
                )}
              </div>
            </div>
            <RenderWhen>
              <RenderWhen.If isTrue={nftMarket.asset.prompt !== null}>
                {nftMarket?.amount === null ? (
                  <>
                    <div>
                      <p className="text-[12px] text-gray-500">Price</p>
                      <p className="text-[13px] font-semibold leading-[1.6]">Free</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-[12px] text-gray-500">Price</p>
                    <p className="text-[13px] font-semibold leading-[1.6]">
                      ${' '}
                      {currencyFormatter(
                        getPriceUsdWithDenom(nftMarket.amount, nftMarket.denom as TokenDenom),
                        { significantDigits: 3 },
                      )}
                    </p>
                  </div>
                )}
              </RenderWhen.If>
            </RenderWhen>

            <RenderWhen>
              <RenderWhen.If isTrue={nftMarket.asset.prompt === null}>
                {nftMarket?.amount === null ? (
                  <>
                    <div>
                      <p className="text-[12px] text-gray-500">Price</p>
                      <p className="text-[13px] font-semibold leading-[1.6]">Free</p>
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-[12px] text-gray-500">Price</p>
                    <p className="text-[13px] font-semibold leading-[1.6]">
                      {nftMarket.denom === TokenDenom.CUPI ? (
                        <>
                          {currencyFormatter(nftMarket.amount, { significantDigits: 3 })}{' '}
                          {nftMarket.denom}
                        </>
                      ) : (
                        <>
                          {currencyFormatter(nftMarket.amount, { significantDigits: 3 })}
                          {nftMarket.denom === 'AIRI' ? <span> AIRI</span> : <span> $</span>}
                        </>
                      )}
                    </p>
                  </div>
                )}
              </RenderWhen.If>
            </RenderWhen>

            <Tooltip
              content={
                <div className="w-full px-[4px] py-[16px]">
                  <div className="flex items-center pb-[16px]">
                    <div className="flex items-center rounded-[39px] bg-[#F8F9FD] px-[8px] py-[4px]">
                      <div className=" flex items-center justify-center">
                        <div className="pr-[3px]">
                          <IconEyeCard />
                        </div>
                        <span className="font-sans text-[12px]">
                          {formatNumberSocial(nftMarket.totalView)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="">
                    <div className="pb-[16px]">
                      <span className="font-sora font-semibold leading-6 text-[#181818]">
                        {nftMarket.asset?.name || '--'}
                      </span>
                      <span className="cursor-pointer pl-[5px] font-sora font-semibold leading-6 text-gray-500">
                        #{nftMarket.tokenId}
                      </span>
                      <div>
                        <span className="font-sora font-normal leading-6 text-[#777E90]">
                          Created by{' '}
                        </span>
                        <span
                          className="cursor-pointer font-sora font-semibold leading-6 text-[#DA613A]"
                          onClick={(event) => {
                            event.preventDefault();
                            router.push(`/profile/${nftMarket.asset.provider.publicAddress}`);
                            event.stopPropagation();
                          }}
                        >
                          {nftMarket.asset.provider.username}
                        </span>
                      </div>
                    </div>
                    <hr className="mb-[16px] bg-[#F0F2FA]" />

                    <div className="">
                      <div className="flex ">
                        {nftMarket.asset?.collection && (
                          <div className="pb-[16px] pr-[12px]">
                            <div className="font-sora font-semibold leading-7 text-[#777E90]">
                              Collection
                            </div>
                            <div
                              className="cursor-pointer rounded-[100px] bg-[#F8F9FD] px-[12px] py-[2px] font-sora font-normal leading-7 text-black"
                              onClick={(event) => {
                                event.preventDefault();
                                router.push(`/collection/${nftMarket.asset.collectionId}`);
                                event.stopPropagation();
                              }}
                            >
                              {nftMarket.asset.collection?.name}
                            </div>
                          </div>
                        )}
                        {nftMarket.asset.promptId && (
                          <div className="pb-[16px]">
                            <div className="font-sora font-semibold leading-7 text-[#777E90]">
                              Prompt
                            </div>
                            <div
                              className="cursor-pointer rounded-[100px] bg-[#F8F9FD] px-[12px] py-[2px] font-sora font-normal leading-7 text-black"
                              onClick={(event) => {
                                event.preventDefault();
                                router.push(
                                  `${
                                    isOwner
                                      ? `/profile/${nftMarket.asset.provider.publicAddress}/prompt/${nftMarket.asset.promptId}`
                                      : `/prompt/${nftMarket.asset.promptId}`
                                  }`,
                                );
                                event.stopPropagation();
                              }}
                            >
                              #{nftMarket.asset.promptId}
                            </div>
                          </div>
                        )}
                      </div>
                      {nftMarket.asset?.collection !== null || nftMarket.asset.promptId !== null ? (
                        <hr className="mb-[16px] bg-[#F0F2FA]" />
                      ) : (
                        ''
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between">
                        <span className="font-sora font-normal leading-7 text-black">
                          Creator’s royalty
                        </span>
                        <span className="pl-[10px] text-right font-sora font-semibold leading-7 text-[#616879]">
                          {convertRoyaltyFromContract(nftMarket.asset.generatorRoyalty || 0)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-sora font-normal leading-7 text-black">
                          Owner’s royalty
                        </span>
                        <span className="pl-[10px] text-right font-sora font-semibold leading-7 text-[#616879]">
                          {convertRoyaltyFromContract(nftMarket.sellerRoyalty || 0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              }
              className="w-[300px] rounded-[20px] bg-white shadow-md hover:shadow-lg"
            >
              <div>
                <IconInfoMarketPlace />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default MarketPlaceCard;
